import React, { useState, useEffect } from 'react';
import { X, ShoppingCart, Heart, Tag, Package, Info, Check, AlertCircle, Star, MessageSquare, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import WriteReviewModal from './WriteReviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetailModal = ({ 
  product, 
  isOpen, 
  onClose, 
  onAddToCart,
  isFavorite,
  onToggleFavorite,
  user,
  token
}) => {
  const [reviews, setReviews] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [ratingBreakdown, setRatingBreakdown] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && product?.id) {
      fetchProductReviews();
    }
  }, [isOpen, product?.id]);

  const fetchProductReviews = async () => {
    if (!product?.id) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews/product/${product.id}?limit=5`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setRatingBreakdown(data.rating_breakdown || {});
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating, size = 'w-4 h-4') => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (!isOpen || !product) return null;

  const averageRating = product.average_rating || 0;
  const reviewCount = product.review_count || reviews.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden z-10">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        {/* Product Image */}
        <div className="relative h-64 bg-gradient-to-br from-gray-100 to-gray-200">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain p-4"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
            }}
          />
          
          {/* Stock Badge */}
          <div className="absolute top-4 left-4">
            {product.inStock ? (
              <Badge className="bg-green-500 text-white flex items-center gap-1">
                <Check className="w-3 h-3" />
                In Stock
              </Badge>
            ) : (
              <Badge className="bg-red-500 text-white flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Out of Stock
              </Badge>
            )}
          </div>

          {/* Favorite Button */}
          {onToggleFavorite && (
            <button
              onClick={() => onToggleFavorite(product.id)}
              className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
            >
              <Heart 
                className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`} 
              />
            </button>
          )}
        </div>

        {/* Product Info */}
        <div className="p-6">
          {/* Category & Region */}
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs">
              {product.category}
            </Badge>
            {product.region && (
              <span className="text-lg">{product.region}</span>
            )}
            {product.taxable && (
              <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                +13% HST
              </Badge>
            )}
          </div>

          {/* Name */}
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {product.name}
          </h2>

          {/* Price */}
          <div className="flex items-baseline gap-2 mb-4">
            <span className="text-3xl font-bold text-orange-600">
              ${product.price?.toFixed(2)}
            </span>
            {product.taxable && (
              <span className="text-sm text-gray-500">
                (${(product.price * 1.13).toFixed(2)} with tax)
              </span>
            )}
          </div>

          {/* Description/Details */}
          <div className="space-y-3 mb-6">
            {product.description ? (
              <p className="text-gray-600">{product.description}</p>
            ) : (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
                  <Info className="w-4 h-4" />
                  Product Details
                </h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Package className="w-4 h-4 text-gray-400" />
                    Category: <span className="font-medium capitalize">{product.category}</span>
                  </li>
                  {product.barcode && (
                    <li className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-400" />
                      Barcode: <span className="font-mono text-xs">{product.barcode}</span>
                    </li>
                  )}
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-gray-400" />
                    Quality: Fresh & Authentic
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Add to Cart Button */}
          <Button
            onClick={() => {
              onAddToCart(product);
              onClose();
            }}
            disabled={!product.inStock}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg font-semibold"
            data-testid="add-to-cart-btn"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            {product.inStock ? 'Add to Cart' : 'Out of Stock'}
          </Button>

          {/* Reviews Section */}
          <div className="mt-4 border-t pt-4">
            {/* Rating Summary */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {renderStars(Math.round(averageRating))}
                <span className="font-semibold">{averageRating.toFixed(1)}</span>
                <span className="text-sm text-gray-500">({reviewCount} reviews)</span>
              </div>
              {user && token && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowWriteReview(true)}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="write-review-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Write Review
                </Button>
              )}
            </div>

            {/* Toggle Reviews */}
            {reviews.length > 0 && (
              <button
                onClick={() => setShowReviews(!showReviews)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 mb-3"
                data-testid="toggle-reviews-btn"
              >
                {showReviews ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showReviews ? 'Hide Reviews' : `Show ${reviews.length} Reviews`}
              </button>
            )}

            {/* Reviews List */}
            {showReviews && (
              <div className="space-y-3 max-h-48 overflow-y-auto">
                {reviews.map((review) => (
                  <div key={review.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {renderStars(review.rating, 'w-3 h-3')}
                        {review.verified_purchase && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(review.created_at)}</span>
                    </div>
                    <p className="font-medium text-sm text-gray-900">{review.title}</p>
                    <p className="text-xs text-gray-600 line-clamp-2">{review.comment}</p>
                    <p className="text-xs text-gray-400 mt-1">— {review.user_name}</p>
                  </div>
                ))}
              </div>
            )}

            {/* No Reviews Yet */}
            {reviews.length === 0 && !loading && (
              <p className="text-sm text-gray-500 text-center py-2">
                No reviews yet. {user ? 'Be the first to review!' : 'Sign in to write a review.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Write Review Modal */}
      <WriteReviewModal
        isOpen={showWriteReview}
        onClose={() => setShowWriteReview(false)}
        product={product}
        token={token}
        onSuccess={fetchProductReviews}
      />
    </div>
  );
};

export default ProductDetailModal;
